import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { dropLegacyVestCatSettings } from './vestCatLegacy';

// Level types that can be toggled on/off
export type LevelType =
  | 'sessionHodLod'   // Session HOD/LOD (full session)
  | 'rthHodLod'       // RTH HOD/LOD (8:30 AM - 3:15 PM CT)
  | 'prevDayHodLod'   // Previous Day High/Low
  | 'ibRange'         // Initial Balance (8:30-9:30 AM CT)
  | 'onhOnl'          // Overnight high/low (5:00 PM - 8:30 AM CT)
  | 'midnightOpen'    // Midnight Open (11 PM CT)
  | 'mocOpen'         // MOC imbalance: open of the 12:50 PM PT / 2:50 PM CT candle
  | 'prevWeekHodLod'  // Previous Week High/Low
  | 'currWeekHodLod'  // Current Week High/Low
  | 'prevMonthHodLod' // Previous Month High/Low
  | 'fibonacci'        // Fibonacci Retracement Levels
  | 'donchian'        // Donchian Channels
  | 'ema9'            // EMA 9
  | 'ema21'           // EMA 21
  | 'ema200'          // EMA 200
  | 'vwap'            // Session VWAP
  | 'absorption'      // Absorption-at-level candle markers ("ABS PW")
  | 'vestVolume'      // Vest NQ-PERP volume (2m bars) as thin bars in the CME volume pane, MNQ/NQ only
  | 'cmeVolSpikes'    // "2.4x" + brightened bar on unusual CME volume, in the volume pane
  | 'vestVolSpikes'   // 🐋 over unusual Vest bars in the volume pane, MNQ/NQ only
  | 'cmeSpikes'       // 🦈 on candles with unusual CME volume
  | 'bothSpikes'      // 🐋🦈 on candles where both spiked with agreeing deltas, MNQ/NQ only
  | 'vestSpikes'      // 🐋 on candles with unusually high Vest NQ-PERP volume, MNQ/NQ only
  | 'vestCat'         // 🐱 on candles whose Vest bar sticks out above the CME bar (volume pane), MNQ/NQ only
  | 'wickRejection'   // 🪝 at the tip of a long rejection wick (possible reversal), display only
  | 'vestAtrSizing'   // ATR TP/SL + size suggestion lines and legend, .VEST charts only (#637)
  // Order-flow markers from flow-signals (flowSignalsStore), set from the
  // TradeFinder panel: a master switch, then one toggle per kind group
  | 'flowMarkers'       // master: every TradeFinder marker on the chart
  | 'flowAbsorption'    // absorption
  | 'flowSweep'         // sweep incl. Sweep-S; passive/stop sweep traces follow it
  | 'flowStopRun'       // stop_run
  | 'flowStopVolume'    // stop_volume
  | 'flowVolumeSpike'   // volume_spike
  | 'flowImbalance'     // imbalance_signal (market volume imbalance)
  | 'flowIceberg'       // iceberg, iceberg_development, iceberg_cancelled
  | 'flowBookImbalance' // book_imbalance
  | 'flowLiquidity'     // liquidity
  // Context: research-backed reads, not triggers (never alert, never chime)
  | 'flowSweepFollow'   // sweep_follow (⏩ + 60 s timer ring), unproven: off by default
  | 'flowQueueTilt'     // queue_tilt line: band at the price scale + per-bar ribbon, ES/MES only
  | 'flowBurstLevel'    // burst_level: burst into a level, NQ/MNQ (flow-signals root default)
  | 'flowAbsBreakLevel' // abs_break_level: absorption break at a level, NQ/MNQ
  | 'flowQueueDepletion' // queue_depletion: touch queue emptied, ES/MES
  // Order-flow line panes under the price (flowLinesStore), set from the
  // TradeFinder panel's Lines tab; independent of the marker master switch
  | 'flowLineMvi'           // market_volume_imbalance (shares a pane with stop volume)
  | 'flowLineStopVolume'    // stop_volume
  | 'flowLineBookImbalance' // order_book_imbalance (own pane)
  | 'flowLineAvgLiquidity'  // average_liquidity (shares a pane with price dynamics)
  | 'flowLinePriceDynamics';// price_dynamics

// Level color configuration
export interface LevelColors {
  sessionHodLod: { high: string; low: string };
  rthHodLod: { high: string; low: string };
  prevDayHodLod: { high: string; low: string };
  ibRange: { high: string; low: string };
  onhOnl: { high: string; low: string };
  midnightOpen: string;
  mocOpen: string;
  prevWeekHodLod: { high: string; low: string };
  currWeekHodLod: { high: string; low: string };
  prevMonthHodLod: { high: string; low: string };
  fibonacci: string;
  donchian: string;
  ema9: string;
  ema21: string;
  ema200: string;
  vwap: string;
  /** high = bearish (above the candle), low = bullish (below the candle). */
  absorption: { high: string; low: string };
  /** high = taker buys > sells, low = sells > buys (drawn at 50% alpha). */
  vestVolume: { high: string; low: string };
  /** Not drawn (the whale is an emoji); kept so every level has a colour slot. */
  vestSpikes: string;
  cmeVolSpikes: string;
  vestVolSpikes: string;
  cmeSpikes: string;
  bothSpikes: string;
  vestCat: string;
  wickRejection: string;
  /** Not used for drawing (the suggestion lines use faint TP/SL greens and reds); kept so every level has a slot. */
  vestAtrSizing: string;
  /** TradeFinder accent (the master switch draws nothing itself). */
  flowMarkers: string;
  /** Order flow: high = bearish reading (above the price), low = bullish (below). */
  flowAbsorption: { high: string; low: string };
  flowSweep: { high: string; low: string };
  flowStopRun: { high: string; low: string };
  flowStopVolume: { high: string; low: string };
  flowVolumeSpike: { high: string; low: string };
  flowImbalance: { high: string; low: string };
  flowIceberg: { high: string; low: string };
  flowBookImbalance: { high: string; low: string };
  flowLiquidity: { high: string; low: string };
  flowSweepFollow: { high: string; low: string };
  flowBurstLevel: { high: string; low: string };
  flowAbsBreakLevel: { high: string; low: string };
  flowQueueDepletion: { high: string; low: string };
  /** Queue tilt band: high = negative tilt (ask queue heavier), low = positive (bid heavier). */
  flowQueueTilt: { high: string; low: string };
  /** Order-flow line colours (one per line). */
  flowLineMvi: string;
  flowLineStopVolume: string;
  flowLineBookImbalance: string;
  flowLineAvgLiquidity: string;
  flowLinePriceDynamics: string;
}

interface LevelsState {
  // Visibility toggles for each level type
  visibility: Record<LevelType, boolean>;

  // Color configuration
  colors: LevelColors;

  // Actions
  toggleLevel: (level: LevelType) => void;
  setLevelVisible: (level: LevelType, visible: boolean) => void;
  setAllLevelsVisible: (visible: boolean) => void;
  setLevelColor: (level: LevelType, color: string, isHigh?: boolean) => void;
  resetColors: () => void;
}

/** Order-flow (TradeFinder) toggles: kept out of the Levels panel and its bulk actions. */
export function isOrderFlowLevel(key: string): boolean {
  return key.startsWith('flow');
}

/** Order-flow markers: red for a bearish reading, green for bullish. */
const FLOW_COLORS = { high: '#ef4444', low: '#22c55e' };

// Default colors
export const defaultColors: LevelColors = {
  sessionHodLod: { high: '#22c55e', low: '#ef4444' },     // Green/Red for session
  rthHodLod: { high: '#3b82f6', low: '#f97316' },         // Blue/Orange for RTH
  prevDayHodLod: { high: '#8b5cf6', low: '#ec4899' },     // Purple/Pink for PDH/PDL
  ibRange: { high: '#14b8a6', low: '#f59e0b' },           // Teal/Amber for IB
  onhOnl: { high: '#a3e635', low: '#fb7185' },            // Lime/Rose for ONH/ONL
  midnightOpen: '#a855f7',                                // Purple for midnight open
  mocOpen: '#ff5a1f',                                     // Hot red-orange for MOC imbalance
  prevWeekHodLod: { high: '#06b6d4', low: '#e11d48' },    // Cyan/Rose for prev week
  currWeekHodLod: { high: '#10b981', low: '#e11d48' },    // Emerald/Rose for curr week
  prevMonthHodLod: { high: '#d946ef', low: '#f97316' },   // Fuchsia/Orange for prev month
  fibonacci: '#fbbf24',                                    // Amber for fib levels
  donchian: '#06b6d4',                                     // Cyan for donchian channels
  ema9: '#60a5fa',                                         // Light blue for EMA 9
  ema21: '#fbbf24',                                        // Amber for EMA 21
  ema200: '#a855f7',                                       // Purple for EMA 200
  vwap: '#f97316',                                         // Orange for VWAP
  absorption: { high: '#ff5370', low: '#2ee6a6' },         // Bear red above / bull green below
  vestVolume: { high: '#26a69a', low: '#ef5350' },         // CME volume pane green / red
  vestSpikes: '#38bdf8',
  cmeVolSpikes: '#e2e8f0',
  vestVolSpikes: '#fbbf24',
  cmeSpikes: '#94a3b8',
  bothSpikes: '#38bdf8',
  vestCat: '#f472b6',
  wickRejection: '#f59e0b',
  vestAtrSizing: '#9ca3af',
  flowMarkers: '#38bdf8',
  flowAbsorption: { ...FLOW_COLORS },
  flowSweep: { ...FLOW_COLORS },
  flowStopRun: { ...FLOW_COLORS },
  flowStopVolume: { ...FLOW_COLORS },
  flowVolumeSpike: { ...FLOW_COLORS },
  flowImbalance: { ...FLOW_COLORS },
  flowIceberg: { ...FLOW_COLORS },
  flowBookImbalance: { ...FLOW_COLORS },
  flowLiquidity: { ...FLOW_COLORS },
  flowSweepFollow: { ...FLOW_COLORS },
  flowBurstLevel: { ...FLOW_COLORS },
  flowAbsBreakLevel: { ...FLOW_COLORS },
  flowQueueDepletion: { ...FLOW_COLORS },
  flowQueueTilt: { ...FLOW_COLORS },
  flowLineMvi: '#38bdf8',
  flowLineStopVolume: '#f59e0b',
  flowLineBookImbalance: '#a78bfa',
  flowLineAvgLiquidity: '#34d399',
  flowLinePriceDynamics: '#f472b6',
};

// Default visibility (on, except the noisier order-flow kinds)
export const defaultVisibility: Record<LevelType, boolean> = {
  sessionHodLod: true,
  rthHodLod: true,
  prevDayHodLod: true,
  ibRange: true,
  onhOnl: true,
  midnightOpen: true,
  mocOpen: true,
  prevWeekHodLod: true,
  currWeekHodLod: true,
  prevMonthHodLod: true,
  fibonacci: true,
  donchian: true,
  ema9: true,
  ema21: true,
  ema200: true,
  vwap: true,
  absorption: true,
  vestVolume: true,
  vestSpikes: true,
  cmeVolSpikes: true,
  vestVolSpikes: true,
  cmeSpikes: true,
  bothSpikes: true,
  vestCat: true,
  wickRejection: true,
  vestAtrSizing: true,
  // Order flow: the high-signal kinds on, the chatty ones off.
  flowMarkers: true,
  flowAbsorption: true,
  flowSweep: true,
  flowStopRun: true,
  flowStopVolume: true,
  flowVolumeSpike: true,
  flowImbalance: false,
  flowIceberg: false,
  flowBookImbalance: false,
  flowLiquidity: false,
  // Context. sweep_follow is unproven (full-history research): off. The
  // validated ones are on; each only has data on the roots flow-signals runs
  // it for (queue tilt / depletion ES+MES, burst / abs break NQ+MNQ).
  // mergePersistedLevels gives a key missing from the save its default, and
  // LEVELS_DEFAULTS_REV re-defaults keys whose default changed.
  flowSweepFollow: false,
  flowQueueTilt: true,
  flowBurstLevel: true,
  flowAbsBreakLevel: true,
  flowQueueDepletion: true,
  // Order-flow line panes: all off until asked for.
  flowLineMvi: false,
  flowLineStopVolume: false,
  flowLineBookImbalance: false,
  flowLineAvgLiquidity: false,
  flowLinePriceDynamics: false,
};

/**
 * Visibility defaults that changed after users could save the old one. A save
 * older than `rev` takes the new default for these keys once; the next save
 * records LEVELS_DEFAULTS_REV, after which the user's choice sticks again.
 */
export const LEVELS_DEFAULT_CHANGES: readonly { rev: number; keys: readonly LevelType[] }[] = [
  // 2026-10-13: queue tilt validated on 2024-2025 ES/MES history -> on by default.
  { rev: 1, keys: ['flowQueueTilt'] },
];
export const LEVELS_DEFAULTS_REV = 1;

/**
 * Merge persisted levels state onto the current (default) state.
 *
 * Levels added after a user's state was saved (e.g. `mocOpen`, `onhOnl`) fall
 * back to their default visibility; known keys keep the user's saved boolean;
 * unknown or non-boolean entries are dropped so counts in the Levels menu stay
 * right; keys in LEVELS_DEFAULT_CHANGES newer than the save take the new
 * default. Saved colours are merged the same way: only well-formed values for
 * known levels are taken, everything else keeps its default.
 */
export function mergePersistedLevels<T extends { visibility: Record<LevelType, boolean>; colors?: LevelColors }>(
  persisted: unknown,
  current: T,
): T {
  const p = (persisted && typeof persisted === 'object' ? persisted : {}) as {
    visibility?: unknown; colors?: unknown; defaultsRev?: unknown;
  };
  const saved = p.visibility;
  const visibility = { ...defaultVisibility, ...current.visibility };
  const rev = typeof p.defaultsRev === 'number' ? p.defaultsRev : 0;
  // Keys whose default changed after the save: the old saved value was the old default, not a choice.
  const reDefault = new Set<string>(LEVELS_DEFAULT_CHANGES.filter((c) => c.rev > rev).flatMap((c) => c.keys));
  if (saved && typeof saved === 'object') {
    for (const key of Object.keys(defaultVisibility) as LevelType[]) {
      if (reDefault.has(key)) continue;
      const v = (saved as Record<string, unknown>)[key];
      if (typeof v === 'boolean') visibility[key] = v;
    }
  }
  if (!current.colors) return { ...current, visibility };
  const colors = { ...defaultColors, ...current.colors } as LevelColors;
  const savedColors = p.colors;
  if (savedColors && typeof savedColors === 'object') {
    const isColor = (v: unknown): v is string => typeof v === 'string' && v.length > 0;
    for (const key of Object.keys(defaultColors) as (keyof LevelColors)[]) {
      const v = (savedColors as Record<string, unknown>)[key];
      const def = defaultColors[key];
      if (typeof def === 'string') {
        if (isColor(v)) (colors as unknown as Record<string, unknown>)[key] = v;
      } else if (v && typeof v === 'object') {
        const hl = v as { high?: unknown; low?: unknown };
        (colors as unknown as Record<string, unknown>)[key] = {
          high: isColor(hl.high) ? hl.high : def.high,
          low: isColor(hl.low) ? hl.low : def.low,
        };
      }
    }
  }
  return { ...current, visibility, colors };
}

export const useLevelsStore = create<LevelsState>()(
  persist(
    (set) => ({
      visibility: { ...defaultVisibility },
      colors: { ...defaultColors },

      toggleLevel: (level: LevelType) =>
        set((state) => ({
          visibility: {
            ...state.visibility,
            [level]: !state.visibility[level],
          },
        })),

      setLevelVisible: (level: LevelType, visible: boolean) =>
        set((state) => ({
          visibility: {
            ...state.visibility,
            [level]: visible,
          },
        })),

      // Order-flow toggles live in the TradeFinder panel and are never part
      // of the Levels panel's Show / Hide everything.
      setAllLevelsVisible: (visible: boolean) =>
        set((state) => ({
          visibility: Object.fromEntries(
            (Object.keys(defaultVisibility) as LevelType[]).map((k) => [
              k,
              isOrderFlowLevel(k) ? state.visibility[k] ?? defaultVisibility[k] : visible,
            ]),
          ) as Record<LevelType, boolean>,
        })),

      setLevelColor: (level: LevelType, color: string, isHigh?: boolean) =>
        set((state) => {
          if (level === 'midnightOpen' || level === 'mocOpen' || level === 'fibonacci' || level === 'donchian'
              || level === 'ema9' || level === 'ema21' || level === 'ema200' || level === 'vwap' || level === 'vestSpikes'
              || level === 'cmeVolSpikes' || level === 'vestVolSpikes' || level === 'cmeSpikes' || level === 'bothSpikes' || level === 'vestCat' || level === 'wickRejection' || level === 'vestAtrSizing'
              || level === 'flowMarkers' || level === 'flowLineMvi' || level === 'flowLineStopVolume'
              || level === 'flowLineBookImbalance' || level === 'flowLineAvgLiquidity' || level === 'flowLinePriceDynamics') {
            return {
              colors: {
                ...state.colors,
                [level]: color,
              },
            };
          }

          const levelColors = state.colors[level];
          if (typeof levelColors === 'object' && 'high' in levelColors) {
            return {
              colors: {
                ...state.colors,
                [level]: {
                  ...levelColors,
                  [isHigh ? 'high' : 'low']: color,
                },
              },
            };
          }
          return state;
        }),

      resetColors: () =>
        set({ colors: { ...defaultColors } }),
    }),
    {
      name: 'levels-visibility',
      // Visibility, plus the colours the menu lets you edit (ONH/ONL, absorption) so a
      // picked colour survives a reload. Only those: persisting every colour
      // would freeze today's defaults in each browser forever.
      partialize: (state) => ({
        defaultsRev: LEVELS_DEFAULTS_REV,
        visibility: state.visibility,
        colors: { onhOnl: state.colors.onhOnl, absorption: state.colors.absorption },
      }),
      // The default merge is shallow, so a persisted `visibility` saved before a
      // level existed would replace the defaults wholesale and leave the new key
      // undefined (= hidden). Merge per key instead.
      merge: (persisted, current) => mergePersistedLevels(persisted, current),
    }
  )
);

// The 🐱 threshold store is gone (#599); its toggle is `vestCat` above.
dropLegacyVestCatSettings();
